import { RJSFSchema } from "@rjsf/utils";
import { JSONValue } from "@/src/types/components/ScenarioForm";

/**
 * Extracts indicator mappings from schema allOf conditions.
 * Looks for if/then patterns that map maatregel values to indicators.
 * 
 * @param schema - The RJSF schema to analyze
 * @returns A mapping of maatregel values to their associated indicator field names
 */
export const extractIndicatorMappings = (schema: RJSFSchema): Record<string, string[]> => {
  const mappings: Record<string, string[]> = {};
  
  const processProperties = (properties: Record<string, unknown>) => {
    if (!properties) return;
    
    Object.values(properties).forEach((property: unknown) => {
      const prop = property as Record<string, unknown>;
      if (prop.type === 'array' && (prop.items as Record<string, unknown>)?.default_indicators) {
        const allOfConditions = (prop.items as Record<string, unknown>).default_indicators as Array<Record<string, unknown>>;
        allOfConditions.forEach((condition: Record<string, unknown>) => {
          const ifCondition = condition.if as Record<string, unknown>;
          const thenCondition = condition.then as Record<string, unknown>;
          
          if (ifCondition?.properties && thenCondition?.properties) {
            const properties = ifCondition.properties as Record<string, unknown>;
            const maatregelProp = properties.maatregel as Record<string, unknown>;
            const thenProperties = thenCondition.properties as Record<string, unknown>;
            
            if (maatregelProp?.const && thenProperties) {
              const maatregelValue = maatregelProp.const as string;
              const indicators = Object.keys(thenProperties).filter(key => 
                key.startsWith('indicator') && !key.endsWith('_score')
              );
              if (indicators.length > 0) {
                mappings[maatregelValue] = indicators;
              }
            }
          }
        });
      } else if (prop.type === 'object' && prop.properties) {
        processProperties(prop.properties as Record<string, unknown>);
      }
    });
  };
  
  if (schema.properties) {
    processProperties(schema.properties as Record<string, unknown>);
  }
  return mappings;
};

/**
 * Keeps the hidden template ID in sync with the selected measure name.
 * The measure select stores only the name in RJSF form data, while the
 * template ID is metadata on the schema option.
 */
export const syncMeasureTemplateIds = (
  formData: JSONValue,
  schema: RJSFSchema
): JSONValue => {
  if (!formData || typeof formData !== 'object' || Array.isArray(formData)) {
    return formData;
  }

  const updatedData = { ...formData } as Record<string, unknown>;

  Object.keys(updatedData).forEach((categoryKey) => {
    const categoryData = updatedData[categoryKey];
    const categorySchema = schema.properties?.[categoryKey] as RJSFSchema | undefined;
    const itemSchema = categorySchema?.items as RJSFSchema | undefined;
    const measureSchema = itemSchema?.properties?.maatregel as RJSFSchema | undefined;
    const options = measureSchema?.options;

    if (!Array.isArray(categoryData) || !Array.isArray(options)) {
      return;
    }

    updatedData[categoryKey] = categoryData.map((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return item;
      }

      const measure = item as Record<string, unknown>;
      const selectedOption = options.find(
        (option) =>
          option &&
          typeof option === 'object' &&
          (option as Record<string, unknown>).value === measure.maatregel
      ) as Record<string, unknown> | undefined;

      if (!selectedOption) {
        return measure;
      }

      const templateId = selectedOption.template_id;
      return Object.assign({}, measure, {
        template_id: typeof templateId === 'number' ? templateId : null,
      });
    });
  });

  return updatedData as JSONValue;
};

/**
 * Updates indicator values based on maatregel selection.
 * Only updates indicators when the maatregel field itself changes, 
 * preserving user manual changes to indicators.
 * 
 * @param newFormData - The new form data
 * @param previousFormData - The previous form data to compare against
 * @param schema - The RJSF schema containing the indicator mappings
 * @returns Updated form data with indicators set according to maatregel selections
 */
export const updateIndicatorsBasedOnMaatregel = (
  newFormData: JSONValue, 
  previousFormData: JSONValue | null,
  schema: RJSFSchema
): JSONValue => {
  if (!newFormData || typeof newFormData !== 'object') return newFormData;

  const formDataWithTemplateIds = syncMeasureTemplateIds(newFormData, schema);
  
  const indicatorMappings = extractIndicatorMappings(schema);
  
  // Early return if no indicator mappings found (not a maatregelen form)
  if (Object.keys(indicatorMappings).length === 0) {
    return formDataWithTemplateIds;
  }
  
  const updatedData = Object.assign(
    {},
    formDataWithTemplateIds
  ) as Record<string, unknown>;
  const prevData = (previousFormData || {}) as Record<string, unknown>;
  
  // Function to check if maatregel changed in an item
  const didMaatregelChange = (newItem: Record<string, unknown>, prevItem?: Record<string, unknown>): boolean => {
    // If no previous data exists, we need to check if this is truly a new selection
    // or just the initial load with existing data
    if (!prevItem) {
      // If the item has both a maatregel and indicator values already set,
      // this is likely an initial load with saved data - don't treat as a change
      const hasExistingIndicators = Object.keys(newItem).some(key => 
        key.startsWith('indicator') && !key.endsWith('_score') && newItem[key] === true
      );
      
      // Only treat as a change if maatregel exists but no indicators are set
      // (true first-time selection)
      return !!newItem.maatregel && !hasExistingIndicators;
    }
    return newItem.maatregel !== prevItem.maatregel;
  };
  
  // Function to process each maatregel category
  const processMaatregelenCategory = (
    categoryData: unknown[], 
    prevCategoryData?: unknown[]
  ): unknown[] => {
    if (!Array.isArray(categoryData)) return categoryData;
    
    return categoryData.map((item: unknown, index: number) => {
      if (!item || typeof item !== 'object') return item;
      
      const itemData = item as Record<string, unknown>;
      const prevItemData = prevCategoryData?.[index] as Record<string, unknown> | undefined;
      
      // Only update indicators if the maatregel actually changed
      if (!didMaatregelChange(itemData, prevItemData)) {
        return itemData; // Return unchanged if maatregel didn't change
      }
      
      const updatedItem = { ...itemData };
      const selectedMaatregel = itemData.maatregel as string;
      
      if (selectedMaatregel && indicatorMappings[selectedMaatregel]) {
        // Set the mapped indicators to true
        indicatorMappings[selectedMaatregel].forEach(indicator => {
          updatedItem[indicator] = true;
        });
        
        // Reset other indicators that might have been set by other maatregelen
        Object.keys(updatedItem).forEach(key => {
          if (key.startsWith('indicator') && !key.endsWith('_score')) {
            if (!indicatorMappings[selectedMaatregel].includes(key)) {
              // Only reset if it's not explicitly mapped to the current maatregel
              updatedItem[key] = false;
            }
          }
        });
      } else if (selectedMaatregel) {
        // If maatregel is selected but has no indicator mappings, reset all indicators
        Object.keys(updatedItem).forEach(key => {
          if (key.startsWith('indicator') && !key.endsWith('_score')) {
            updatedItem[key] = false;
          }
        });
      }
      
      return updatedItem;
    });
  };
  
  // Process all maatregel categories in the form data
  Object.keys(updatedData).forEach(key => {
    if (Array.isArray(updatedData[key])) {
      const prevCategoryData = Array.isArray(prevData[key]) ? prevData[key] as unknown[] : undefined;
      updatedData[key] = processMaatregelenCategory(
        updatedData[key] as unknown[], 
        prevCategoryData
      );
    }
  });
  
  return updatedData as JSONValue;
};

/**
 * Checks if a form schema contains maatregel-related fields.
 * Used to determine if indicator processing should be applied.
 * 
 * @param schema - The RJSF schema to check
 * @returns True if the schema contains maatregel fields
 */
export const isMaatregelenForm = (schema: RJSFSchema): boolean => {
  return schema.title?.toLowerCase().includes('maatregelen') || 
         JSON.stringify(schema).includes('maatregel');
};